import React, { useEffect, useState } from "react";
import { ChakraProvider, Box, extendTheme, Textarea } from "@chakra-ui/react";
import { useMediaQuery } from "@chakra-ui/media-query";
import { Navigate, Routes, Route } from "react-router-dom";
import { PROTECTED_PATHS } from "./constants";
import { Navigation } from "../components/nav";
import SideBar from "../components/layout/sidebar";
import { Dashboard } from "../pages/dashboard/";
import { Profile } from "../pages/profile";
import { Usage } from "../pages/usage";
import { TopUp } from "../pages/topUp";

import { StepsStyleConfig as Steps } from "chakra-ui-steps";
import { Notification } from "../pages/notification";
import { UserProfileContextProvider } from "../context/UserContext";
import { Messages } from "../pages/message";
import { VtuPage } from "../pages/vtu";
import { TransactionsHistoryView } from "../pages/vtu/TransactionsHistoryView";
import { CustomerCareView } from "../pages/vtu/CustomerCareView";
import SelectAccount from "../pages/account/SelectAccount";
import { PRIMARY_HEX, PRIMARY_PALETTE } from "../theme/primary";
import ClearTamper from "../components/ClearTamper";
import { Faq } from "../pages/faq";
import InvitationPreview from "../pages/invitation/InvitationPreview";
import RequireAccountGate from "../components/RequireAccountGate";
import PaymentRecords from "../pages/paymentRecords";

const AuthenticatedApp = () => {
  const [toggleSide, setToggleSide] = useState(false);
  const [showSidebar] = useState(true);
  const [isMobileScreen] = useMediaQuery("(max-width: 600px)");
  const [isMidScreen] = useMediaQuery(
    "(min-width: 601px) and (max-width: 1024px)",
  );
  // const pageColor = useSelector(
  //   (state) => state?.switchTab?.currentDisplay?.pageColor
  // );
  const theme = extendTheme({
    components: {
      Steps,
      Button: {
        variants: {
          solid: {
            borderRadius: "default",
            bg: "primary.500",
            color: "white",
            _hover: {
              bg: "primary.600",
            },
          },
        },
      },
      Input: {
        variants: {
          outline: {
            field: {
              borderRadius: "default",
            },
          },
        },
      },
      Textarea: {
        variants: {
          outline: {
            field: {
              borderRadius: "default",
            },
          },
        },
      },
      Select: {
        variants: {
          outline: {
            field: {
              borderRadius: "default",
            },
          },
        },
      },
    },
    colors: {
      primary: PRIMARY_PALETTE,
      primaryDark: "rgb(3, 23, 48)",
      secondary: "#FC7067",
      bgColor: "#EFE2FF",
      bgColorLight: "#F9F9F9",
      lightPink: "#FEE8E7",
      pageColor: PRIMARY_HEX,
      brand: PRIMARY_PALETTE,
      blue: PRIMARY_PALETTE,
      green: PRIMARY_PALETTE,
      cyan: PRIMARY_PALETTE,
    },
    radii: {
      default: "20px",
    },
  });

  const {
    DASHBOARD,
    VTU,
    VTU_TRANSACTIONS_HISTORY,
    VTU_CUSTOMER_CARE,
    PROFILE,
    MESSAGES,
    // USAGE,
    BUY_WATER,
    NOTIFICATION,
    FAQ,
    PAYMENT_RECORDS,
    CLEAR_TAMPER,
  } = PROTECTED_PATHS;

  const handleToggle = () => {
    setToggleSide((initial) => !initial);
  };

  useEffect(() => {
    if (isMobileScreen) {
      setToggleSide((initial) => !initial);
    }
  }, [isMobileScreen]);

  // Auto close sidebar when viewport is in mid (tablet) range
  useEffect(() => {
    if (isMidScreen) {
      // ensure sidebar is closed on mid viewports
      setToggleSide(true);
    } else if (!isMobileScreen) {
      // restore open sidebar on larger screens
      setToggleSide(false);
    }
  }, [isMidScreen, isMobileScreen]);

  return (
    <UserProfileContextProvider>
      <ChakraProvider theme={theme}>
        <Box className="App" display="flex">
          <Box className={`app-container`}>
            <Box
              className="app-sidebar"
              display={`${isMobileScreen ? "none" : toggleSide ? "none" : "block"}`}
            >
              {showSidebar ? <SideBar toggle={handleToggle} /> : ""}
            </Box>
            {/* app right */}
            <Box w="100%" h="100%">
              <Navigation
                toggle={handleToggle}
                SideBarActive={toggleSide}
                showSidebar={showSidebar}
              />
              <Box
                width="100%"
                maxWidth="100%"
                mt="70px"
                padding={["5px", "10px", "20px"]}
              >
                <Routes>
                  <Route path={"/"} element={<Dashboard />} />
                  <Route path={DASHBOARD} element={<Dashboard />} />
                  <Route path={VTU} exact element={<VtuPage />} />
                  <Route
                    path={VTU_TRANSACTIONS_HISTORY}
                    exact
                    element={<TransactionsHistoryView />}
                  />
                  <Route
                    path={VTU_CUSTOMER_CARE}
                    exact
                    element={<CustomerCareView />}
                  />
                  <Route path={BUY_WATER} exact element={<TopUp />} />
                  <Route
                    path={PAYMENT_RECORDS}
                    exact
                    element={<PaymentRecords />}
                  />
                  <Route path="/select-account" element={<SelectAccount />} />
                  {/* <Route path={USAGE} exact element={<Usage />} /> */}
                  <Route path={PROFILE} exact element={<Profile />} />
                  <Route path={MESSAGES} exact element={<Messages />} />
                  <Route path={NOTIFICATION} exact element={<Notification />} />
                  <Route path={CLEAR_TAMPER} element={<ClearTamper />} />
                  <Route path={FAQ} exact element={<Faq />} />
                  <Route
                    path="/invitations/:token"
                    element={<InvitationPreview />}
                  />
                  <Route
                    path="/*"
                    element={<Navigate replace to={DASHBOARD} />}
                  />
                </Routes>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* render overlay sidebar on mobile when toggled open */}
        {isMobileScreen && !toggleSide && showSidebar && (
          <SideBar toggle={handleToggle} />
        )}
        <RequireAccountGate />
      </ChakraProvider>
    </UserProfileContextProvider>
  );
};

export default AuthenticatedApp;
